/** What people can be notified about (brief section 25.2), with sensible defaults per channel. */
export const NOTIFICATION_CATEGORIES = [
  "mention",
  "reply",
  "task_assigned",
  "task_due",
  "currently_with",
  "new_brief",
  "brief_deadline",
  "demo_uploaded",
  "song_comment",
  "brief_song",
  "pitch_sent",
  "pitch_feedback",
  "change_requested",
  "pitch_accepted",
  "pitch_rejected",
  "project_stale",
  "call_started",
  "call_scheduled",
  "call_reminder",
  "system",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export interface ChannelPrefs {
  inApp: boolean;
  push: boolean;
  email: boolean;
}

export const NOTIFICATION_CATEGORY_INFO: Record<
  NotificationCategory,
  {
    label: string;
    hint: string;
    defaults: ChannelPrefs;
    /** Only admins receive it (and only they see it in notification settings). */
    adminOnly?: boolean;
  }
> = {
  mention: {
    label: "Mentions",
    hint: "Someone @mentions you",
    defaults: { inApp: true, push: true, email: false },
  },
  reply: {
    label: "Replies",
    hint: "Replies to your messages and comments",
    defaults: { inApp: true, push: true, email: false },
  },
  task_assigned: {
    label: "Tasks for you",
    hint: "A task is assigned to you",
    defaults: { inApp: true, push: true, email: false },
  },
  task_due: {
    label: "Tasks due",
    hint: "Your open tasks due today or tomorrow",
    defaults: { inApp: true, push: true, email: false },
  },
  currently_with: {
    label: "Songs handed to you",
    hint: "A song is now with you",
    defaults: { inApp: true, push: true, email: false },
  },
  new_brief: {
    label: "New briefs",
    hint: "Someone adds a brief",
    defaults: { inApp: true, push: false, email: false },
  },
  brief_deadline: {
    label: "Brief deadlines",
    hint: "An exact brief deadline is two days away",
    defaults: { inApp: true, push: true, email: false },
  },
  demo_uploaded: {
    label: "New demos",
    hint: "New demo versions on songs you're involved in",
    defaults: { inApp: true, push: false, email: false },
  },
  song_comment: {
    label: "Song discussion",
    hint: "Comments and messages on songs you're involved in",
    defaults: { inApp: true, push: false, email: false },
  },
  brief_song: {
    label: "Songs added to briefs",
    hint: "A song you're involved in is being considered for a brief",
    defaults: { inApp: true, push: false, email: false },
  },
  pitch_sent: {
    label: "Pitches sent",
    hint: "Songs you're involved in are pitched",
    defaults: { inApp: true, push: false, email: false },
  },
  pitch_feedback: {
    label: "Pitch feedback",
    hint: "Feedback arrives on a pitch",
    defaults: { inApp: true, push: false, email: false },
  },
  change_requested: {
    label: "Change requests",
    hint: "A pitch asks for changes",
    defaults: { inApp: true, push: true, email: false },
  },
  pitch_accepted: {
    label: "Accepted songs",
    hint: "A song is accepted",
    defaults: { inApp: true, push: true, email: false },
  },
  pitch_rejected: {
    label: "Rejections",
    hint: "A pitched song is turned down",
    defaults: { inApp: true, push: false, email: false },
  },
  project_stale: {
    label: "Quiet projects",
    hint: "A project has had no activity for a while",
    defaults: { inApp: true, push: false, email: false },
  },
  call_started: {
    label: "Calls",
    hint: "A call starts in a place you're involved in",
    defaults: { inApp: true, push: true, email: false },
  },
  call_scheduled: {
    label: "Planned calls",
    hint: "A call you're part of is planned, changed or called off (emails include a calendar file)",
    defaults: { inApp: true, push: false, email: true },
  },
  call_reminder: {
    label: "Call reminders",
    hint: "Before a planned call starts; choose when below",
    defaults: { inApp: true, push: true, email: false },
  },
  system: {
    label: "System alerts",
    hint: "Backups that failed and a background worker that stopped",
    defaults: { inApp: true, push: false, email: true },
    adminOnly: true,
  },
};

/**
 * How notification settings are presented: a handful of rows instead of one per category. Storage
 * stays per category; setting a group writes the same choice to each of its categories. Every
 * category belongs to exactly one group.
 */
export const NOTIFICATION_GROUPS = [
  {
    id: "mentions",
    label: "Mentions and replies",
    hint: "Someone @mentions you or replies to you",
    categories: ["mention", "reply"],
  },
  {
    id: "tasks",
    label: "Your tasks",
    hint: "Tasks for you, tasks due soon, songs handed to you",
    categories: ["task_assigned", "task_due", "currently_with"],
  },
  {
    id: "songs",
    label: "Songs and demos",
    hint: "New demo versions and discussion on songs you're involved in",
    categories: ["demo_uploaded", "song_comment"],
  },
  {
    id: "pitching",
    label: "Briefs and pitches",
    hint: "New briefs and deadlines, songs considered or pitched, feedback and answers",
    categories: [
      "new_brief",
      "brief_deadline",
      "brief_song",
      "pitch_sent",
      "pitch_feedback",
      "change_requested",
      "pitch_accepted",
      "pitch_rejected",
    ],
  },
  {
    id: "team",
    label: "Team and calls",
    hint: "Calls starting, planned calls and their reminders, quiet projects",
    categories: ["call_started", "call_scheduled", "call_reminder", "project_stale"],
  },
  {
    id: "system",
    label: "System alerts",
    hint: "Backups that failed and a background worker that stopped",
    categories: ["system"],
    adminOnly: true,
  },
] as const satisfies readonly {
  id: string;
  label: string;
  hint: string;
  categories: readonly NotificationCategory[];
  adminOnly?: boolean;
}[];
type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];
type NotificationGroupId = NotificationGroup["id"];
export const NOTIFICATION_GROUP_IDS = NOTIFICATION_GROUPS.map((g) => g.id) as [
  NotificationGroupId,
  ...NotificationGroupId[],
];

/** When people can be reminded before a planned call, in minutes. */
export const CALL_REMINDER_OPTIONS = [
  { minutes: 0, label: "When it starts" },
  { minutes: 5, label: "5 minutes before" },
  { minutes: 15, label: "15 minutes before" },
  { minutes: 30, label: "30 minutes before" },
  { minutes: 60, label: "1 hour before" },
  { minutes: 1440, label: "1 day before" },
] as const;

/** Categories that belong in "Needs your attention" on Home. */
export const ATTENTION_CATEGORIES: NotificationCategory[] = [
  "mention",
  "reply",
  "task_assigned",
  "task_due",
  "currently_with",
  "change_requested",
  "pitch_feedback",
  "pitch_accepted",
  "pitch_rejected",
];
